import { useEffect, useMemo, useState } from 'react';
import { HardwareFieldDefinition } from '../../services/typesService';
import { groupSchema, hasPasswordField, loadTemplateSchema } from './templateSchema';

/**
 * The configuration schema of the selected gateway type: loaded whenever
 * the type changes (and the dialog is open), grouped for the editor, with
 * the product's base configuration alongside. Shared by the create and
 * the detail dialogs.
 *
 * `schema` is null before a type is picked, an empty array when the type
 * has no fields; `onLoaded` lets the caller prefill values from it.
 */
export const useTemplateSchema = (deviceType: string, enabled: boolean, onLoaded?: (fields: HardwareFieldDefinition[]) => void) => {
  const [schema, setSchema] = useState<HardwareFieldDefinition[] | null>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [productConfig, setProductConfig] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (!enabled) return;
    if (!deviceType) {
      setSchema(null);
      setProductConfig({});
      return;
    }
    let cancelled = false;
    setSchemaLoading(true);
    loadTemplateSchema(deviceType)
      .then(({ fields, productConfig: product }) => {
        if (cancelled) return;
        setSchema(fields);
        setProductConfig(product);
        onLoaded?.(fields);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[CONFIG TEMPLATE] Failed to fetch schema:', err);
        setSchema([]);
      })
      .finally(() => { if (!cancelled) setSchemaLoading(false); });
    return () => { cancelled = true; };
    // onLoaded is a prefill callback; re-running on its identity would reload the schema on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceType, enabled]);

  const groupedSchema = useMemo(() => groupSchema(schema), [schema]);
  const passwordInSchema = useMemo(() => hasPasswordField(schema), [schema]);

  return { schema, schemaLoading, productConfig, groupedSchema, passwordInSchema };
};
